import { z } from 'zod';

// Kept in sync with SKILL_TYPES in apps/api/src/core/skills/schema.ts — the
// client package doesn't depend on the api app, so this is intentionally a
// small, duplicated constant (same pattern as the rest of this file mirroring
// apps/api/src/core/skills/validators.ts rather than importing it).
export const SKILL_TYPES = ['skill', 'application'] as const;
export type SkillType = (typeof SKILL_TYPES)[number];

export const SkillSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  type: z.enum(SKILL_TYPES),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Skill = z.infer<typeof SkillSchema>;

const SkillBody = z.object({
  name: z.string().min(1).max(120),
  description: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  type: z.enum(SKILL_TYPES).optional(),
  active: z.boolean().optional(),
});

export const CreateSkillInput = SkillBody;
export type CreateSkillInput = z.infer<typeof CreateSkillInput>;

export const PatchSkillInput = SkillBody.partial();
export type PatchSkillInput = z.infer<typeof PatchSkillInput>;

export interface ListSkillsQuery {
  q?: string;
  active?: boolean;
  type?: SkillType;
  category?: string;
  sortField?: 'name' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface SkillCategorySummary {
  category: string | null;
  type: SkillType;
  count: number;
}

// ---- Import / Export ------------------------------------------------------

/** Export filter — mirrors the Master list's on-screen filters (minus
 *  pagination) so the downloaded file matches what the user sees. */
export type ExportSkillsQuery = Pick<ListSkillsQuery, 'q' | 'active' | 'type' | 'category'>;

/** Result of POST /api/skills/import — one entry per row in the uploaded
 *  file (in source order). Mirrors the products/users shape; the generic
 *  `ImportSheet` consumes either. */
export type ImportSkillRowOutcome = 'created' | 'updated' | 'failed';

export interface ImportSkillsRow {
  rowNumber: number;
  /** Name parsed from the row, used as the upsert key. Null when the row
   *  was missing/garbled. */
  name: string | null;
  outcome: ImportSkillRowOutcome;
  errors?: string[];
}

export interface ImportSkillsResult {
  totalRows: number;
  created: number;
  updated: number;
  failed: number;
  rows: ImportSkillsRow[];
}
